import { useEffect, useId, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from '../../lib/hooks.js';
import { pushOverlay } from '../../lib/overlays.js';
import { IconButton } from './Button.js';

/**
 * Bottom sheet on phones (drag the grabber or header down to dismiss), a side
 * panel or centred modal on wider screens. Stays mounted through its exit
 * animation. Escape closes; focus moves into the sheet and back on close.
 */
export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  icon,
  actions,
  children,
  desktop = 'side',
  wide = false,
  testId,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  desktop?: 'side' | 'modal';
  wide?: boolean;
  testId?: string;
}): ReactNode {
  const isPhone = useMediaQuery('(max-width: 719px)');
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const restore = useRef<HTMLElement | null>(null);
  const dragRef = useRef<{ y: number; dy: number; t: number } | null>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) {
      restore.current = document.activeElement as HTMLElement | null;
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      const t = window.setTimeout(() => {
        setMounted(false);
        setClosing(false);
        restore.current?.focus?.();
      }, 240);
      return () => window.clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!mounted || closing) return;
    const p = panel.current;
    p?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    const pop = pushOverlay(() => onCloseRef.current());
    return () => {
      window.removeEventListener('keydown', onKey);
      pop();
    };
  }, [mounted, closing]);

  if (!mounted) return null;

  const onDragStart = (e: PointerEvent<HTMLElement>): void => {
    if (!isPhone || (e.target as HTMLElement).closest('button, a, input')) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { y: e.clientY, dy: 0, t: performance.now() };
    panel.current?.setAttribute('data-dragging', 'true');
  };
  const onDragMove = (e: PointerEvent<HTMLElement>): void => {
    const d = dragRef.current;
    if (!d || !panel.current) return;
    d.dy = Math.max(0, e.clientY - d.y);
    // Resist upward pulls, follow downward ones 1:1.
    panel.current.style.transform = `translate3d(0, ${d.dy}px, 0)`;
  };
  const onDragEnd = (): void => {
    const d = dragRef.current;
    const p = panel.current;
    dragRef.current = null;
    if (!d || !p) return;
    p.removeAttribute('data-dragging');
    const velocity = d.dy / Math.max(1, performance.now() - d.t);
    if (d.dy > 120 || velocity > 0.6) {
      onCloseRef.current();
    } else {
      p.style.transform = '';
    }
  };

  const variant = isPhone ? 'bottom' : desktop;

  return createPortal(
    <div className="sheet-root" data-variant={variant} data-closing={closing ? 'true' : undefined}>
      <div className="sheet-scrim" onClick={() => onCloseRef.current()} aria-hidden="true" />
      <div
        ref={panel}
        className="sheet glass glass-raised"
        data-wide={wide ? 'true' : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        data-testid={testId}
      >
        <header
          className="sheet-head"
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          {variant === 'bottom' && <span className="sheet-grabber" aria-hidden="true" />}
          <div className="sheet-head-row">
            {icon}
            <div className="sheet-titles">
              {title && (
                <h2 id={titleId} className="sheet-title">
                  {title}
                </h2>
              )}
              {subtitle && <div className="sheet-sub">{subtitle}</div>}
            </div>
            <span className="spacer" />
            {actions}
            <IconButton icon="close" label="Close" size={34} onClick={() => onCloseRef.current()} />
          </div>
        </header>
        <div className="sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
